import React, { useState, useEffect } from 'react';
import { SITE_CONFIG } from '../config';

const PrivateListingForm = ({ show, onClose, onSuccess }) => {
  const [privateListingData, setPrivateListingData] = useState({
    listing_type: 'sale',
    status: 'available',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    postal_code: '',
    neighborhood: '',
    property_type: 'residential',
    beds: 0,
    baths: 0,
    square_footage: '',
    year_built: '',
    price_sale: '',
    price_lease_monthly: '',
    lease_term_months: '',
    security_deposit: '',
    headline: '',
    description: '',
    virtual_tour_url: '',
    is_featured: false
  });
  const [listingImages, setListingImages] = useState([]);
  const [existingListings, setExistingListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      fetchExistingListings();
    }
  }, [show]);

  const fetchExistingListings = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: { 'x-api-key': 'treas-api-key-2025' }
      });
      if (response.ok) {
        const data = await response.json();
        setExistingListings(data.listings || []);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = async (e) => {
    const listingId = e.target.value;
    if (!listingId) {
      setSelectedListingId(null);
      resetForm();
      return;
    }

    const listing = existingListings.find(l => l.id === parseInt(listingId));
    if (listing) {
      setSelectedListingId(listing.id);
      setPrivateListingData({
        listing_type: listing.listing_type,
        status: listing.status || 'available',
        address_line_1: listing.address_line_1,
        address_line_2: listing.address_line_2 || '',
        city: listing.city,
        state_province: listing.state_province,
        postal_code: listing.postal_code,
        neighborhood: listing.neighborhood || '',
        property_type: listing.property_type,
        beds: listing.beds,
        baths: listing.baths,
        square_footage: listing.square_footage || '',
        year_built: listing.year_built || '',
        price_sale: listing.price_sale || '',
        price_lease_monthly: listing.price_lease_monthly || '',
        lease_term_months: listing.lease_term_months || '',
        security_deposit: listing.security_deposit || '',
        headline: listing.headline || '',
        description: listing.description || '',
        virtual_tour_url: listing.virtual_tour_url || '',
        is_featured: listing.is_featured || false
      });

      try {
        const response = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${listingId}`, {
          headers: { 'x-api-key': 'treas-api-key-2025' }
        });
        if (response.ok) {
          const data = await response.json();
          const images = (data.media || []).map(img => ({
            file: null,
            preview: img.s3_link,
            order: img.order_hint,
            existing: true,
            id: img.id
          }));
          setListingImages(images);
        }
      } catch (error) {
        console.error('Error fetching media:', error);
      }
    }
  };

  const resetForm = () => {
    setPrivateListingData({
      listing_type: 'sale',
      status: 'available',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      neighborhood: '',
      property_type: 'residential',
      beds: 0,
      baths: 0,
      square_footage: '',
      year_built: '',
      price_sale: '',
      price_lease_monthly: '',
      lease_term_months: '',
      security_deposit: '',
      headline: '',
      description: '',
      virtual_tour_url: '',
      is_featured: false
    });
    setListingImages([]);
    setSelectedListingId(null);
  };

  const submitListing = async () => {
    try {
      const payload = {
        site_id: SITE_CONFIG.SITE_ID,
        ...privateListingData,
        beds: parseInt(privateListingData.beds) || 0,
        baths: parseFloat(privateListingData.baths) || 0,
        square_footage: privateListingData.square_footage ? parseInt(privateListingData.square_footage) : null,
        year_built: privateListingData.year_built ? parseInt(privateListingData.year_built) : null,
        price_sale: privateListingData.price_sale ? parseFloat(privateListingData.price_sale) : null,
        price_lease_monthly: privateListingData.price_lease_monthly ? parseFloat(privateListingData.price_lease_monthly) : null,
        lease_term_months: privateListingData.lease_term_months ? parseInt(privateListingData.lease_term_months) : null,
        security_deposit: privateListingData.security_deposit ? parseFloat(privateListingData.security_deposit) : null
      };

      const url = selectedListingId 
        ? `https://tophamtechbe.com/api/treas/private/v1/listing/${selectedListingId}`
        : 'https://tophamtechbe.com/api/treas/private/v1/listing';

      const response = await fetch(url, {
        method: selectedListingId ? 'PUT' : 'POST',
        headers: {
          'x-api-key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const listingId = selectedListingId || result.listing_id;
        
        const newImages = listingImages.filter(img => !img.existing);
        if (newImages.length > 0) {
          const formData = new FormData();
          formData.append('site_id', SITE_CONFIG.SITE_ID);
          formData.append('listing_id', listingId);
          newImages.forEach((img, index) => {
            formData.append('images', img.file);
            formData.append(`order_hint_${index}`, index + 1);
          });
          await fetch('https://tophamtechbe.com/api/treas/media/private/v1/upload-media', {
            method: 'POST',
            headers: { 'x-api-key': 'treas-api-key-2025' },
            body: formData
          });
        }
        
        resetForm();
        onSuccess();
      } else {
        const error = await response.json();
        alert(`Failed to ${selectedListingId ? 'update' : 'add'} listing: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting listing:', error);
      alert('Failed to submit listing. Please try again.');
    }
  };

  const deleteListing = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listing/${selectedListingId}`, {
        method: 'DELETE',
        headers: { 'x-api-key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        setShowDeleteConfirm(false);
        resetForm();
        onSuccess();
      } else {
        alert('Failed to delete listing');
      }
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing');
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file, index) => ({
      file,
      preview: URL.createObjectURL(file),
      order: listingImages.length + index,
      existing: false
    }));
    setListingImages([...listingImages, ...newImages]);
  };

  const removeImage = (index) => {
    const imageToRemove = listingImages[index];
    if (imageToRemove.existing && imageToRemove.id) {
      if (!window.confirm('Delete this image permanently?')) return;
      fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${imageToRemove.id}`, {
        method: 'DELETE',
        headers: { 'x-api-key': 'treas-api-key-2025' }
      }).catch(error => console.error('Error deleting image:', error));
    }
    setListingImages(listingImages.filter((_, i) => i !== index));
  };

  const moveImage = async (index, direction) => {
    const newImages = [...listingImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newImages.length) {
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      setListingImages(newImages);

      const existingImages = newImages.filter(img => img.existing && img.id);
      if (existingImages.length > 0) {
        const updates = existingImages.map((img, idx) => ({
          id: img.id,
          order_hint: idx + 1
        }));
        try {
          await fetch('https://tophamtechbe.com/api/treas/media/private/v1/media/reorder', {
            method: 'PUT',
            headers: {
              'x-api-key': 'treas-api-key-2025',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ updates })
          });
        } catch (error) {
          console.error('Error reordering images:', error);
        }
      }
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content private-listing-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h3 style={{margin: 0}}>{selectedListingId ? 'Edit' : 'Add'} Private Listing{selectedListingId ? <span style={{fontSize: '13px', color: '#888', fontWeight: 'normal', marginLeft: '8px'}}>ID: {selectedListingId}</span> : null}</h3>
            <select value={selectedListingId || ''} onChange={handleListingSelect} style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}>
              <option value="">New Listing</option>
              {existingListings.map(listing => (
                <option key={listing.id} value={listing.id}>{listing.address_line_1}</option>
              ))}
            </select>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Listing Type*</label>
              <select value={privateListingData.listing_type} onChange={(e) => setPrivateListingData({...privateListingData, listing_type: e.target.value})}>
                <option value="sale">Sale</option>
                <option value="lease">Lease</option>
              </select>
            </div>
            <div className="form-group">
              <label>Property Type*</label>
              <select value={privateListingData.property_type} onChange={(e) => setPrivateListingData({...privateListingData, property_type: e.target.value})}>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="land">Land</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status*</label>
              <select value={privateListingData.status} onChange={(e) => setPrivateListingData({...privateListingData, status: e.target.value})}>
                <option value="available">Available</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
            </div>
            <div className="form-group full-width">
              <label>Address*</label>
              <input type="text" value={privateListingData.address_line_1} onChange={(e) => setPrivateListingData({...privateListingData, address_line_1: e.target.value})} placeholder="123 Main St" />
            </div>
            <div className="form-group full-width">
              <label>Address Line 2</label>
              <input type="text" value={privateListingData.address_line_2} onChange={(e) => setPrivateListingData({...privateListingData, address_line_2: e.target.value})} placeholder="Apt, Suite, etc." />
            </div>
            <div className="form-group">
              <label>City*</label>
              <input type="text" value={privateListingData.city} onChange={(e) => setPrivateListingData({...privateListingData, city: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Neighborhood</label>
              <input type="text" value={privateListingData.neighborhood} onChange={(e) => setPrivateListingData({...privateListingData, neighborhood: e.target.value})} />
            </div>
            <div className="form-group">
              <label>State*</label>
              <input type="text" value={privateListingData.state_province} onChange={(e) => setPrivateListingData({...privateListingData, state_province: e.target.value})} maxLength="2" placeholder="TX" />
            </div>
            <div className="form-group">
              <label>Zip Code*</label>
              <input type="text" value={privateListingData.postal_code} onChange={(e) => setPrivateListingData({...privateListingData, postal_code: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Beds</label>
              <input type="number" value={privateListingData.beds} onChange={(e) => setPrivateListingData({...privateListingData, beds: e.target.value})} min="0" />
            </div>
            <div className="form-group">
              <label>Baths</label>
              <input type="number" step="0.5" value={privateListingData.baths} onChange={(e) => setPrivateListingData({...privateListingData, baths: e.target.value})} min="0" />
            </div>
            <div className="form-group">
              <label>Square Footage</label>
              <input type="number" value={privateListingData.square_footage} onChange={(e) => setPrivateListingData({...privateListingData, square_footage: e.target.value})} placeholder="2000" />
            </div>
            <div className="form-group">
              <label>Year Built</label>
              <input type="number" value={privateListingData.year_built} onChange={(e) => setPrivateListingData({...privateListingData, year_built: e.target.value})} placeholder="2020" />
            </div>
            <div className="form-group">
              <label>{privateListingData.listing_type === 'sale' ? 'Sale Price' : 'Monthly Rent'}</label>
              <input type="number" value={privateListingData.listing_type === 'sale' ? privateListingData.price_sale : privateListingData.price_lease_monthly} onChange={(e) => setPrivateListingData({...privateListingData, [privateListingData.listing_type === 'sale' ? 'price_sale' : 'price_lease_monthly']: e.target.value})} placeholder="450000" />
            </div>
            {privateListingData.listing_type === 'lease' && (
              <>
                <div className="form-group">
                  <label>Lease Term (Months)</label>
                  <input type="number" value={privateListingData.lease_term_months} onChange={(e) => setPrivateListingData({...privateListingData, lease_term_months: e.target.value})} placeholder="12" />
                </div>
                <div className="form-group">
                  <label>Security Deposit</label>
                  <input type="number" value={privateListingData.security_deposit} onChange={(e) => setPrivateListingData({...privateListingData, security_deposit: e.target.value})} placeholder="2000" />
                </div>
              </>
            )}
            <div className="form-group full-width">
              <label>Headline</label>
              <input type="text" value={privateListingData.headline} onChange={(e) => setPrivateListingData({...privateListingData, headline: e.target.value})} placeholder="Beautiful Home" />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea value={privateListingData.description} onChange={(e) => setPrivateListingData({...privateListingData, description: e.target.value})} placeholder="Property description..." rows="3" style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit', resize: 'vertical'}} />
            </div>
            <div className="form-group full-width">
              <label>Virtual Tour URL</label>
              <input type="url" value={privateListingData.virtual_tour_url} onChange={(e) => setPrivateListingData({...privateListingData, virtual_tour_url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="form-group full-width">
              <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                <input type="checkbox" checked={privateListingData.is_featured} onChange={(e) => setPrivateListingData({...privateListingData, is_featured: e.target.checked})} />
                <span>Featured Listing</span>
              </label>
            </div>
            <div className="form-group full-width">
              <label>Images</label>
              <div className="file-upload-wrapper">
                <label htmlFor="listing-images" className="file-upload-button">📷 Choose Images</label>
                <input id="listing-images" type="file" accept="image/*" multiple onChange={handleImageSelect} style={{display: 'none'}} />
                <span className="file-upload-text">{listingImages.length} image(s) selected</span>
              </div>
              {listingImages.length > 0 && (
                <div className="image-preview-grid">
                  {listingImages.map((img, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={img.preview} alt={`Preview ${index + 1}`} />
                      <div className="image-controls">
                        <button type="button" onClick={() => moveImage(index, 'up')} disabled={index === 0}>←</button>
                        <span>#{index + 1}</span>
                        <button type="button" onClick={() => moveImage(index, 'down')} disabled={index === listingImages.length - 1}>→</button>
                        <button type="button" onClick={() => removeImage(index)} className="remove-img">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-buttons">
            <button onClick={submitListing} className="submit-issue-btn">{selectedListingId ? 'Update' : 'Add'} Listing</button>
            {selectedListingId && (
              <button onClick={() => setShowDeleteConfirm(true)} className="confirm-cancel-btn">Delete</button>
            )}
            <button onClick={onClose} className="modal-cancel-btn">Cancel</button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Listing</h3>
            <p>Are you sure you want to delete this listing? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button onClick={deleteListing} className="confirm-cancel-btn">Delete</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="modal-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrivateListingForm;
